'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import useGoogleTracking from '@hooks/useGoogleTracking'
import useHandleCouponOrder from '@hooks/useHandleCouponOrder'
import Link from 'next/link'
import React, { useState } from 'react'
import { type Resolver, useForm } from 'react-hook-form'
import * as yup from 'yup'

import {
  ButtonModifier,
  ButtonTypes,
  IconAligns,
} from '@components/atoms/Button/Button'
import Button from '@components/atoms/Button'
import IconArrowRight from '@components/atoms/Icons/IconArrowRight'
import IconCircleX from '@components/atoms/Icons/IconCircleX'
import InputCheckbox from '@components/atoms/Form/InputCheckbox'
import InputText from '@components/atoms/Form/InputText'
import Text from '@components/atoms/Text'
import { TextVariants } from '@components/atoms/Text/Text'

import { RoutesSunpoint } from '@definitions/routes/RoutesSunpoint'
import { Coupon, Studio } from '@definitions/types/symfonyTypes'

import { cn } from '@helper/utils/cn'
import { emailValidation, privacyValidation } from '@helper/utils/validationSchemas'
import { useThemeKey } from '../../hooks/useThemeKey'

export interface CouponFormType {
  email: string
  privacy: boolean
  newsletter: boolean
}

interface CouponFormProps {
  formId: string
  onClose: () => void
  coupon: Coupon
  studio: Studio
  themeKey?: 'sunpoint' | 'wellmaxx'
}

export function CouponForm({
  formId,
  onClose,
  coupon,
  studio,
  themeKey,
}: CouponFormProps) {
  const [loading, setLoading] = useState<boolean>(false)
  const [formSend, setFormSend] = useState<boolean>(false)
  const [result, setResult] = useState<string | null>(null)
  const { register, handleSubmit, formState } = useForm<CouponFormType>({
    resolver: yupResolver(
      yup.object().shape({
        email: emailValidation,
        privacy: privacyValidation,
        newsletter: yup.boolean(),
      })
    ) as Resolver<CouponFormType>,
  })

  const { trackDownloadCoupon } = useGoogleTracking()
  const currentThemeKey = useThemeKey(themeKey)

  const { handleCouponOrder, handleCouponOrderError } = useHandleCouponOrder(
    (status) => {
      setResult(status)
    },
    () => {
      setResult('error')
      console.info('coupon download failed')
      console.error(handleCouponOrderError)
    }
  )

  const onSubmit = async (data: CouponFormType) => {
    setLoading(true)
    const themeName =
      currentThemeKey[0].toUpperCase() + currentThemeKey.slice(1)
    await handleCouponOrder(
      coupon,
      themeName,
      studio,
      data.email,
      data.newsletter
    )
    trackDownloadCoupon(studio, coupon.title)
    setLoading(false)
    setFormSend(true)
  }

  const renderResult = (status: string | null) => {
    if (status === 'success') {
      return (
        <>
          <Text variant={TextVariants.lead}>
            <b>Vielen Dank!</b>
          </Text>
          <Text variant={TextVariants.paragraph}>
            Der Coupon wird in Kürze an deine E-Mail-Adresse gesendet.
          </Text>
        </>
      )
    } else if (result === 'repeated_use') {
      return (
        <>
          <Text variant={TextVariants.lead}>
            <b>Fehler</b>
          </Text>
          <Text variant={TextVariants.paragraph}>
            Du kannst diesen Coupon nur einmal in der Woche herunterladen.
          </Text>
        </>
      )
    } else {
      return (
        <>
          <Text variant={TextVariants.lead}>
            <b>Es ist ein Fehler aufgetreten</b>
          </Text>
          <Text variant={TextVariants.paragraph}>
            Es ist ein Fehler aufgetreten
          </Text>
        </>
      )
    }
  }

  return (
    <div className="bg-white p-4 relative">
      <button
        onClick={onClose}
        className="absolute right-4 top-4 z-10 bg-transparent border-none cursor-pointer"
        type="button"
        aria-label="Close form"
      >
        <IconCircleX />
      </button>
      {!formSend && (
        <form
          action="./"
          onError={(error) => console.log(error)}
          onSubmit={handleSubmit(onSubmit)}
        >
          <label
            htmlFor="coupon-code"
            className="font-bold block pb-4 pr-12"
          >
            E-Mail-Adresse angeben und Coupon zugesendet bekommen:
          </label>
          <div className="flex items-end">
            <div className="mb-0 flex-1 pr-4">
              <InputText
                register={register('email')}
                forId={`${formId}-email`}
                label="E-Mail-Adresse"
                required
                error={formState.errors.email ?? false}
                errorText={formState.errors?.email?.message}
              />
            </div>
            <div className="hidden lg:flex">
              <Button
                modifier={ButtonModifier.TERTIARY}
                buttonType={ButtonTypes.SUBMIT}
                disabled={formState.isSubmitting}
                loading={loading}
                icon={<IconArrowRight />}
                iconAlign={IconAligns.RIGHT}
                className="text-base h-11"
              >
                Coupon anfordern
              </Button>
            </div>
          </div>
          <div className="flex flex-col">
            <div>
              <InputCheckbox
                register={register('privacy')}
                forId={`${formId}-privacy`}
                error={formState.errors.privacy ?? false}
                errorText={formState.errors?.privacy?.message}
                label={
                  <>
                    Ja, ich habe die&nbsp;
                    <Link href={RoutesSunpoint.SUNPOINT__DATENSCHUTZ} className="text-blue-700 hover:text-blue-800 active:text-red-700">
                      Datenschutzerklärung
                    </Link>
                    &nbsp;gelesen
                  </>
                }
              />
            </div>
            <div>
              <InputCheckbox
                register={register('newsletter')}
                forId={`${formId}-newsletter`}
                error={formState.errors.newsletter ?? false}
                errorText={formState.errors?.newsletter?.message}
                label={
                  <>
                    SUNPOINT Newsletter: Ja, ich möchte wöchentlich über aktuelle
                    Trends, Angebote und Gutscheine per E-Mail informiert werden.
                    Diese Einwilligung kann ich jederzeit gemäß &nbsp;
                    <Link href={RoutesSunpoint.SUNPOINT__DATENSCHUTZ} className="text-blue-700 hover:text-blue-800 active:text-red-700">
                      Datenschutzerklärung
                    </Link>
                    &nbsp; widerrufen.
                  </>
                }
              />
            </div>
          </div>
          <div className="flex mx-auto lg:hidden">
            <Button
              modifier={ButtonModifier.TERTIARY}
              buttonType={ButtonTypes.SUBMIT}
              disabled={formState.isSubmitting}
              loading={loading}
              icon={<IconArrowRight />}
              iconAlign={IconAligns.RIGHT}
              className="text-base mb-0 mr-0 py-2 px-4"
            >
              Coupon anfordern
            </Button>
          </div>
        </form>
      )}
      {formSend && (
        <div
          className={cn(
            'flex flex-col items-center justify-center h-full pt-4',
            result === 'success' ? 'text-green-600' : 'text-red-600'
          )}
        >
          {renderResult(result)}
        </div>
      )}
    </div>
  )
}
